import { useTheme, type Theme } from "@roberttamas/ui";
import { Avatar , Button , Wordmark } from "@roberttamas/ui";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { toast } from "sonner";

import { EmailVerificationBadge } from "@/components/email-verification-badge";
import { Banknote, Globe, Landmark, Languages, LayoutTemplate, LogOut, Monitor, Moon, Package, ShoppingCart, Sun, Users } from "@/components/icons";
import { LanguageSelector } from "@/components/language-selector";
import type { TranslationKey } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { toAppError } from "@/lib/errors";
import { useIsAdmin } from "@/lib/permissions";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  labelKey: TranslationKey;
  icon: typeof Users;
  /** Extra path prefixes that should also mark this item active. */
  match: (pathname: string) => boolean;
}

const ADMIN_NAV_ITEMS: NavItem[] = [
  {
    to: "/sites",
    labelKey: "nav.sites",
    icon: Globe,
    match: (p) => p.startsWith("/sites"),
  },
  {
    to: "/users",
    labelKey: "nav.users",
    icon: Users,
    match: (p) => p.startsWith("/users"),
  },
  {
    to: "/translations",
    labelKey: "nav.translations",
    icon: Languages,
    match: (p) => p.startsWith("/translations"),
  },
  {
    to: "/templates",
    labelKey: "nav.templates",
    icon: LayoutTemplate,
    match: (p) => p.startsWith("/templates"),
  },
  {
    to: "/pricing",
    labelKey: "nav.pricing",
    icon: Banknote,
    match: (p) => p.startsWith("/pricing"),
  },
  {
    to: "/orders",
    labelKey: "nav.orders",
    icon: ShoppingCart,
    match: (p) => p.startsWith("/orders"),
  },
  {
    to: "/billing",
    labelKey: "nav.billing",
    icon: Landmark,
    match: (p) => p.startsWith("/billing"),
  },
];

// Buyers (non-admins) get a much smaller nav: their orders, and a way to
// start a new purchase without leaving the dashboard for the landing page.
const BUYER_NAV_ITEMS: NavItem[] = [
  {
    to: "/buy",
    labelKey: "nav.buy",
    icon: ShoppingCart,
    match: (p) => p.startsWith("/buy") || p.startsWith("/checkout"),
  },
  {
    to: "/my-orders",
    labelKey: "nav.myOrders",
    icon: Package,
    match: (p) => p.startsWith("/my-orders") || p.startsWith("/setup"),
  },
];

const THEME_OPTIONS: { value: Theme; labelKey: TranslationKey; icon: typeof Sun }[] = [
  { value: "light", labelKey: "nav.themeLight", icon: Sun },
  { value: "dark", labelKey: "nav.themeDark", icon: Moon },
  { value: "system", labelKey: "nav.themeSystem", icon: Monitor },
];

interface Props {
  /** Called after a nav item is chosen — closes the mobile drawer. */
  onNavigate?: () => void;
}

/**
 * Primary navigation, shared by every signed-in user — admin or buyer.
 * Rendered fixed-left on desktop and inside a slide-in drawer on mobile
 * (same component, `onNavigate` closes the drawer). Brand at the top,
 * sections in the middle, and a user block (identity + theme + sign out)
 * pinned to the bottom. Which nav items show depends on `useIsAdmin()` —
 * the same admin check that gates the admin-only routes.
 */
export function AppSidebar({ onNavigate }: Props) {
  const location = useLocation();
  const { isAdmin, isLoading } = useIsAdmin();
  const { t } = useTranslation();

  // While the admin check is still in flight, show no items rather than
  // flashing the wrong set once it resolves.
  const navItems = isLoading ? [] : isAdmin ? ADMIN_NAV_ITEMS : BUYER_NAV_ITEMS;

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center px-5">
        <Wordmark strong="raw" rest=".gallery" className="text-3xl" />
      </div>

      <nav className="flex-1 space-y-1 px-3 py-2" aria-label={t("nav.primary")}>
        {navItems.map((item) => {
          const active = item.match(location.pathname);
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                active
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {t(item.labelKey)}
            </NavLink>
          );
        })}
      </nav>

      <UserBlock />
    </div>
  );
}

function UserBlock() {
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  const email = user?.email ?? t("nav.signedIn");
  const initial = (user?.email ?? user?.displayName ?? "?").trim().charAt(0).toUpperCase();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      toast.error(toAppError(err).message);
    }
  };

  return (
    <div className="space-y-3 border-t px-4 py-4">
      <div className="flex items-center gap-3">
        <Avatar className="items-center justify-center bg-muted text-xs font-semibold uppercase text-muted-foreground">
          {user?.photoURL ? (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img src={user.photoURL} className="h-full w-full object-cover" alt="" />
          ) : (
            initial
          )}
        </Avatar>
        <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground" title={email}>
          {email}
        </span>
      </div>

      <EmailVerificationBadge />

      <LanguageSelector />

      <div
        className="grid grid-cols-3 gap-1 rounded-md border p-1"
        role="group"
        aria-label={t("nav.theme")}
      >
        {THEME_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const selected = theme === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTheme(opt.value)}
              aria-pressed={selected}
              className={cn(
                "flex items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium transition-colors",
                selected
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {t(opt.labelKey)}
            </button>
          );
        })}
      </div>

      <Button
        variant="outline"
        size="sm"
        className="w-full justify-start gap-2"
        onClick={handleSignOut}
      >
        <LogOut className="h-4 w-4" />
        {t("nav.signOut")}
      </Button>
    </div>
  );
}
